import { useEffect, useRef, useState } from 'react'
import { cls } from './util'
import { OsmoSymbol } from './brand'
import { CloseIcon } from './icons'

export function Logo({ size = 22 }: { size?: number }) {
  return <span className="logo" style={{ width: size * 1.6 }}><OsmoSymbol title="Osmo" /></span>
}

export function Spinner({ small }: { small?: boolean }) {
  return <span className={cls('spinner', small && 'spinner-sm')} role="status" aria-label="Loading" />
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onClose])
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={cls('modal', wide && 'modal-wide')} role="dialog" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

let toastSet: ((t: { msg: string; kind: 'ok' | 'err'; action?: { label: string; run: () => void } } | null) => void) | null = null
type ToastAction = { label: string; run: () => void }
export function toast(msg: string, kind: 'ok' | 'err' = 'ok', action?: ToastAction) {
  toastSet?.({ msg, kind, action })
}
/** "Removed" with an Undo button for a few seconds. */
export const undoToast = (msg: string, undo: () => void) => toast(msg, 'ok', { label: 'Undo', run: undo })
export function Toaster() {
  const [t, setT] = useState<{ msg: string; kind: 'ok' | 'err'; action?: { label: string; run: () => void } } | null>(null)
  const timer = useRef<number>()
  useEffect(() => {
    toastSet = (x) => {
      setT(x)
      clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setT(null), x?.kind === 'err' || x?.action ? 6000 : 2600)
    }
    return () => {
      toastSet = null
    }
  }, [])
  if (!t) return null
  return (
    <div className={cls('toast', t.kind === 'err' && 'toast-err')} role="status">
      {t.msg}
      {t.action && <button className="toast-act" onClick={() => { t.action!.run(); setT(null) }}>{t.action.label}</button>}
    </div>
  )
}

export function Menu({ label, children, align = 'right', className }: { label: React.ReactNode; children: (close: () => void) => React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    window.addEventListener('mousedown', off)
    return () => window.removeEventListener('mousedown', off)
  }, [open])
  return (
    <div className={cls('menu', className)} ref={ref}>
      <button className="btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}>{label}</button>
      {open && <div className={cls('menu-pop', align === 'left' && 'menu-left')}>{children(() => setOpen(false))}</div>}
    </div>
  )
}
